import { Button, Caption1, MessageBar, MessageBarBody } from '@fluentui/react-components';
import {
  ArrowLeftRegular,
  ArrowRightRegular,
  ArrowUndoRegular,
  CheckmarkRegular,
  DismissRegular,
  PlayRegular,
} from '@fluentui/react-icons';

import { useSubmitAiEditFeedback } from '../aiEdit/api';
import type { TAiEditReviewResult } from '../aiEdit/types';
import useShouldAnimateAiAssistantRun from '../hooks/useShouldAnimateAiAssistantRun.hook';
import type { TAiAssistantSurface, TAiRunFeedbackInput, TAiAssistantOrigin } from '../types';

import AiAssistantSources from './AiAssistantSources';
import AiAssistantDisclaimer from './AiAssistantDisclaimer';
import AiAssistantAnswerCard from './AiAssistantAnswerCard';
import AiAssistantAnswerContent from './AiAssistantAnswerContent';
import AiRunFeedback from './AiRunFeedback';

type TProps = {
  result: TAiEditReviewResult;
  surface: TAiAssistantSurface;
  origin: TAiAssistantOrigin | undefined;
  submittedFeedback: TAiRunFeedbackInput | null;
  onFeedbackSubmitted: (feedback: TAiRunFeedbackInput) => void;
  isBusy?: boolean;
  changeIndex?: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onAccept?: () => void;
  onReject?: () => void;
  onCancel?: () => void;
  onRevert?: () => void;
  onContinue?: () => void;
};

const AiEditReview = ({
  result,
  surface,
  origin,
  submittedFeedback,
  onFeedbackSubmitted,
  isBusy,
  changeIndex = 0,
  onPrevious,
  onNext,
  onAccept,
  onReject,
  onCancel,
  onRevert,
  onContinue,
}: TProps) => {
  const { runId, answer, citationSources, status } = result;
  const changeCount = result.status === 'review' ? result.navigationElementIds.length : 0;
  const saveFailed = result.status === 'accepted' && result.saveFailed;
  const animated = useShouldAnimateAiAssistantRun(runId, surface, origin);
  const feedbackMutation = useSubmitAiEditFeedback();

  return (
    <div className="flex flex-col h-full min-h-0">
      {result.status === 'no-change' && (
        <div className="mx-3 mt-3 shrink-0">
          <MessageBar intent={result.statusMessage.intent} layout="multiline">
            <MessageBarBody>{result.statusMessage.text}</MessageBarBody>
          </MessageBar>
        </div>
      )}
      <AiAssistantAnswerCard
        style={{ flex: '3 1 0%' }}
        footer={
          <AiRunFeedback
            key={runId}
            runId={runId}
            feedbackMutation={feedbackMutation}
            submittedFeedback={submittedFeedback}
            onFeedbackSubmitted={onFeedbackSubmitted}
          />
        }
      >
        <AiAssistantAnswerContent answer={answer} animated={animated} allowLinks={status === 'mock-response'} />
      </AiAssistantAnswerCard>

      <div className="px-4 pt-2 shrink-0">
        <AiAssistantDisclaimer />
      </div>

      <div className="min-h-0 mx-3 pt-3 flex flex-col" style={{ flex: '2 1 0%' }}>
        <div className="flex flex-col gap-3 shrink-0">
          {status === 'review' && changeCount > 1 && (
            <div className="flex items-center justify-between gap-2">
              <Button
                appearance="secondary"
                size="small"
                icon={<ArrowLeftRegular />}
                disabled={isBusy || changeIndex <= 0}
                onClick={onPrevious}
              >
                Previous
              </Button>
              <Caption1>
                {changeIndex + 1} of {changeCount}
              </Caption1>
              <Button
                appearance="secondary"
                size="small"
                icon={<ArrowRightRegular />}
                iconPosition="after"
                disabled={isBusy || changeIndex >= changeCount - 1}
                onClick={onNext}
              >
                Next
              </Button>
            </div>
          )}

          {status === 'review' && result.followUpPrompt && (
            <MessageBar intent="info" layout="multiline">
              <MessageBarBody>This task is not finished yet. Accept all, then Continue does the rest.</MessageBarBody>
            </MessageBar>
          )}

          {status === 'review' && (
            <div className="flex items-center justify-between gap-2">
              <div>
                {onCancel && (
                  <Button appearance="secondary" className="!min-w-0" disabled={isBusy} onClick={onCancel}>
                    Cancel
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  appearance="secondary"
                  className="!min-w-0"
                  icon={<DismissRegular />}
                  disabled={isBusy}
                  onClick={onReject}
                >
                  Reject all
                </Button>
                <Button
                  appearance="primary"
                  className="!min-w-0"
                  icon={<CheckmarkRegular />}
                  disabled={isBusy}
                  onClick={onAccept}
                >
                  Accept all
                </Button>
              </div>
            </div>
          )}

          {status === 'accepted' && (
            <>
              <MessageBar intent={saveFailed ? 'error' : 'success'}>
                <MessageBarBody>
                  {saveFailed
                    ? 'The changes are local but could not be saved.'
                    : 'The changes were accepted and saved.'}
                </MessageBarBody>
              </MessageBar>
              <div className="flex items-center justify-between gap-2">
                <div>
                  {onRevert && (
                    <Button appearance="secondary" icon={<ArrowUndoRegular />} disabled={isBusy} onClick={onRevert}>
                      Revert
                    </Button>
                  )}
                </div>
                <div>
                  {onContinue && (
                    <Button appearance="secondary" icon={<PlayRegular />} disabled={isBusy} onClick={onContinue}>
                      Continue
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
        {citationSources.length > 0 && (
          <div className="min-h-0 flex-1">
            <AiAssistantSources citationSources={citationSources} item={origin?.item} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AiEditReview;
